/**
 * Cart Drawer ADA-Compliant Tab Navigation
 * Manages keyboard navigation flow: Cart Items -> Cart App Input -> Footer Buttons
 */

class CartDrawerTabNavigation {
  constructor() {
    this.cartDrawer = null;
    this.cartItems = null;
    this.cartAppContainer = null;
    this.cartAppFocusableElements = [];
    this.firstAppElement = null;
    this.lastAppElement = null;
    this.footerButtons = null;
    this.lastCartItemFocusable = null;
    this.debug = false; // Set to true for debugging
    
    // Store bound event handlers so we can remove them
    this.handlers = {
      lastCartItemTab: null,
      firstAppElementShiftTab: null,
      lastAppElementTab: null,
      footerButtonShiftTab: null
    };
    
    // Track if we're currently refreshing to avoid double-refresh
    this.isRefreshing = false;
    
    this.init();
  }

  log(...args) {
    if (this.debug) {
      console.log('[Cart Tab Nav]', ...args);
    }
  }

  init() {
    // this.log('Initializing...');
    
    // Try multiple selectors for cart drawer
    this.cartDrawer = document.querySelector('cart-drawer') || 
                      document.querySelector('.cart-drawer') ||
                      document.querySelector('#CartDrawer');
    
    if (!this.cartDrawer) {
      // this.log('Cart drawer not found, retrying...');
      setTimeout(() => this.init(), 1000);
      return;
    }

    // this.log('Cart drawer found');
    this.setupElements();
    this.attachEventListeners();
    this.observeCartChanges();
  }

  setupElements() {
    // this.log('Setting up elements...');

    // Find cart items container
    const cartItemsSelectors = [
      '#CartDrawer-CartItems',
      '.drawer__contents',
      '.cart-items'
    ];

    let cartItemsContainer = null;
    for (const selector of cartItemsSelectors) {
      cartItemsContainer = this.cartDrawer.querySelector(selector);
      if (cartItemsContainer) {
        // this.log('Cart items container found:', selector);
        break;
      }
    }

    // Get cart items (only from within cart table rows)
    if (cartItemsContainer) {
      const cartTable = cartItemsContainer.querySelector('.cart-items') || 
                       cartItemsContainer.querySelector('table');
      
      if (cartTable) {
        const cartRows = cartTable.querySelectorAll('tr.cart-item, .cart-item');
        const focusableElements = [];
        
        cartRows.forEach((row) => {
          const removeBtn = row.querySelector('cart-remove-button[tabindex="0"]');
          const quantityContainer = row.querySelector('quantity-input');
          
          if (removeBtn) {
            focusableElements.push(removeBtn);
          }
          
          if (quantityContainer) {
            const minusBtn = quantityContainer.querySelector('button[name="minus"]:not(.disabled):not([disabled])');
            const input = quantityContainer.querySelector('input.quantity__input:not([disabled])');
            const plusBtn = quantityContainer.querySelector('button[name="plus"]:not(.disabled):not([disabled])');
            
            if (minusBtn) focusableElements.push(minusBtn);
            if (input) focusableElements.push(input);
            if (plusBtn) focusableElements.push(plusBtn);
          }
        });
        
        this.cartItems = focusableElements;
        // this.log(`Found ${this.cartItems.length} focusable cart items`);
      } else {
        // this.log('Cart table not found');
        this.cartItems = [];
      }
      
      if (this.cartItems.length > 0) {
        this.lastCartItemFocusable = this.cartItems[this.cartItems.length - 1];
        // this.log('Last cart item set');
      } else {
        this.lastCartItemFocusable = null;
        // this.log('No focusable cart items found');
      }
    } else {
      // this.log('Cart items container not found');
      this.cartItems = [];
      this.lastCartItemFocusable = null;
    }

    // Find the app container and its focusable elements
    const appSelectors = [
      'cart-drawer-app',
      '#CartDrawerApp',
      '#custom_dropdown'
    ];

    this.cartAppContainer = null;
    this.cartAppFocusableElements = [];
    this.firstAppElement = null;
    this.lastAppElement = null;
    
    for (const selector of appSelectors) {
      const container = document.querySelector(selector);
      
      if (container) {
        const style = window.getComputedStyle(container);
        if (style.display !== 'none' && style.visibility !== 'hidden' && container.offsetParent !== null) {
          this.cartAppContainer = container;
          this.log('App container found:', selector);
          
          // Find all focusable elements within the app container
          const focusableSelectors = [
            'input:not([disabled]):not([tabindex="-1"])',
            'button:not([disabled]):not([tabindex="-1"])',
            'select:not([disabled]):not([tabindex="-1"])',
            'textarea:not([disabled]):not([tabindex="-1"])',
            'a[href]:not([tabindex="-1"])',
            '[tabindex]:not([tabindex="-1"])'
          ];
          
          const elements = Array.from(container.querySelectorAll(focusableSelectors.join(', ')));
          this.cartAppFocusableElements = elements.filter(el => {
            const elStyle = window.getComputedStyle(el);
            return elStyle.display !== 'none' && 
                   elStyle.visibility !== 'hidden' && 
                   el.offsetParent !== null;
          });
          
          if (this.cartAppFocusableElements.length > 0) {
            this.firstAppElement = this.cartAppFocusableElements[0];
            this.lastAppElement = this.cartAppFocusableElements[this.cartAppFocusableElements.length - 1];
            this.log(`Found ${this.cartAppFocusableElements.length} focusable elements in app`);
          }
          
          break;
        }
      }
    }

    if (!this.cartAppContainer) {
      this.log('App container not found');
    }

    // Find footer buttons
    const footerContainer = this.cartDrawer.querySelector('.drawer__footer') ||
                           this.cartDrawer.querySelector('.cart__ctas') ||
                           this.cartDrawer.querySelector('.cartdrawbtn');
    
    if (footerContainer) {
      this.footerButtons = Array.from(footerContainer.querySelectorAll(
        'button:not([disabled]), a.button:not([disabled]), a[href]'
      )).filter(el => {
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && 
               style.visibility !== 'hidden' && 
               el.offsetParent !== null;
      });
      
      // this.log(`Found ${this.footerButtons.length} footer buttons`);
    } else {
      // this.log('Footer container not found');
      this.footerButtons = [];
    }
  }

  removeEventListeners() {
    // this.log('Removing old event listeners...');
    
    if (this.lastCartItemFocusable && this.handlers.lastCartItemTab) {
      this.lastCartItemFocusable.removeEventListener('keydown', this.handlers.lastCartItemTab, true);
    }
    
    if (this.firstAppElement && this.handlers.firstAppElementShiftTab) {
      this.firstAppElement.removeEventListener('keydown', this.handlers.firstAppElementShiftTab, true);
    }
    
    if (this.lastAppElement && this.handlers.lastAppElementTab) {
      this.lastAppElement.removeEventListener('keydown', this.handlers.lastAppElementTab, true);
    }
    
    if (this.footerButtons.length > 0 && this.handlers.footerButtonShiftTab) {
      this.footerButtons[0].removeEventListener('keydown', this.handlers.footerButtonShiftTab, true);
    }
    
    this.handlers = {
      lastCartItemTab: null,
      firstAppElementShiftTab: null,
      lastAppElementTab: null,
      footerButtonShiftTab: null
    };
  }

  attachEventListeners() {
    // this.log('Attaching event listeners...');
    
    this.removeEventListeners();

    // Determine next element after cart items
    const nextAfterCart = this.firstAppElement || (this.footerButtons.length > 0 ? this.footerButtons[0] : null);
    const prevBeforeFooter = this.lastAppElement || this.lastCartItemFocusable;

    // Handler 1: Tab from last cart item to first app element (or footer if no app)
    if (this.lastCartItemFocusable && nextAfterCart) {
      // this.log(`Setting up: Last cart item -> ${this.firstAppElement ? 'First app element' : 'Footer'}`);
      
      this.handlers.lastCartItemTab = (e) => {
        if (e.key === 'Tab' && !e.shiftKey) {
          // this.log('Tab from last cart item');
          e.preventDefault();
          e.stopPropagation();
          nextAfterCart.focus();
        }
      };
      
      this.lastCartItemFocusable.addEventListener('keydown', this.handlers.lastCartItemTab, true);
    }

    // Handler 2: Shift+Tab from first app element back to last cart item
    if (this.firstAppElement && this.lastCartItemFocusable) {
      // this.log('Setting up: First app element <- Last cart item');
      
      this.handlers.firstAppElementShiftTab = (e) => {
        if (e.key === 'Tab' && e.shiftKey) {
          // this.log('Shift+Tab from first app element to cart');
          e.preventDefault();
          e.stopPropagation();
          this.lastCartItemFocusable.focus();
        }
      };
      
      this.firstAppElement.addEventListener('keydown', this.handlers.firstAppElementShiftTab, true);
    }

    // Handler 3: Tab from last app element to footer
    if (this.lastAppElement && this.footerButtons.length > 0) {
      // this.log('Setting up: Last app element -> Footer');
      
      this.handlers.lastAppElementTab = (e) => {
        if (e.key === 'Tab' && !e.shiftKey) {
          // this.log('Tab from last app element to footer');
          e.preventDefault();
          e.stopPropagation();
          this.footerButtons[0].focus();
        }
      };
      
      this.lastAppElement.addEventListener('keydown', this.handlers.lastAppElementTab, true);
    }

    // Handler 4: Shift+Tab from first footer button
    if (this.footerButtons.length > 0 && prevBeforeFooter) {
      // this.log(`Setting up: Footer <- ${this.lastAppElement ? 'Last app element' : 'Cart items'}`);
      
      this.handlers.footerButtonShiftTab = (e) => {
        if (e.key === 'Tab' && e.shiftKey) {
          // this.log('Shift+Tab from footer');
          e.preventDefault();
          e.stopPropagation();
          prevBeforeFooter.focus();
        }
      };
      
      this.footerButtons[0].addEventListener('keydown', this.handlers.footerButtonShiftTab, true);
    }

    // this.log('Event listeners attached');
  }

  observeCartChanges() {
    const cartItemsContainer = this.cartDrawer.querySelector('#CartDrawer-CartItems') ||
                                this.cartDrawer.querySelector('.drawer__contents');
    
    if (!cartItemsContainer) {
      // this.log('Cannot observe: Cart items container not found');
      return;
    }

    // this.log('Observing cart changes');

    const observer = new MutationObserver((mutations) => {
      if (this.isRefreshing) {
        return;
      }

      const hasSignificantChange = mutations.some(mutation => {
        return mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0;
      });
      
      if (hasSignificantChange) {
        // this.log('Cart changed, refreshing...');
        this.isRefreshing = true;
        
        requestAnimationFrame(() => {
          setTimeout(() => {
            this.refresh();
            this.isRefreshing = false;
          }, 300);
        });
      }
    });

    observer.observe(cartItemsContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'disabled', 'hidden']
    });
  }

  refresh() {
    // this.log('Refreshing navigation...');
    this.removeEventListeners();
    this.setupElements();
    this.attachEventListeners();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartDrawerTabNav = new CartDrawerTabNavigation();
  });
} else {
  window.cartDrawerTabNav = new CartDrawerTabNavigation();
}

// Listen for Shopify cart updates
document.addEventListener('DOMContentLoaded', () => {
  // Initial refresh after delay
  setTimeout(() => {
    if (window.cartDrawerTabNav) {
      window.cartDrawerTabNav.refresh();
    }
  }, 2000);
  
  // Listen for cart updated events
  document.addEventListener('cart:updated', () => {
    setTimeout(() => {
      if (window.cartDrawerTabNav) {
        window.cartDrawerTabNav.refresh();
      }
    }, 500);
  });
  
  // Watch for cart drawer opening
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    const drawerObserver = new MutationObserver(() => {
      if (cartDrawer.classList.contains('active') || cartDrawer.classList.contains('animate')) {
        setTimeout(() => {
          if (window.cartDrawerTabNav) {
            window.cartDrawerTabNav.refresh();
          }
        }, 600);
      }
    });
    
    drawerObserver.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['class']
    });
  }
  
  // Listen for item removal
  document.addEventListener('click', (e) => {
    const removeButton = e.target.closest('cart-remove-button');
    if (removeButton) {
      setTimeout(() => {
        if (window.cartDrawerTabNav) {
          window.cartDrawerTabNav.refresh();
        }
      }, 800);
    }
  });

  document.addEventListener('search:results:expanded', () => {
    if (window.cartDrawerTabNav) {
      window.cartDrawerTabNav.refresh();
    }
  });

  document.addEventListener('search:results:collapsed', () => {
    if (window.cartDrawerTabNav) {
      window.cartDrawerTabNav.refresh();
    }
  });
  
});

// Debug helper - run window.debugCartNav() in console to see current state
window.debugCartNav = () => {
  if (window.cartDrawerTabNav) {
    console.log('=== Cart Tab Navigation Debug ===');
    console.log('Cart Items:', window.cartDrawerTabNav.cartItems?.length || 0);
    console.log('Last Cart Item:', window.cartDrawerTabNav.lastCartItemFocusable);
    console.log('App Container:', window.cartDrawerTabNav.cartAppContainer);
    console.log('App Focusable Elements:', window.cartDrawerTabNav.cartAppFocusableElements?.length || 0);
    console.log('First App Element:', window.cartDrawerTabNav.firstAppElement);
    console.log('Last App Element:', window.cartDrawerTabNav.lastAppElement);
    console.log('Footer Buttons:', window.cartDrawerTabNav.footerButtons?.length || 0);
    console.log('Active Handlers:', Object.keys(window.cartDrawerTabNav.handlers).filter(k => window.cartDrawerTabNav.handlers[k] !== null));
    console.log('=================================');
  } else {
    console.log('Cart navigation not initialized');
  }
};